// Navigation der Shells aus den Modul-meta.ts (Registry `navItemsFor`, `adminNavSections`):
// aktiver Eintrag nach der Route, Umwandlung in die Typen von @bar/ui.
import type { AdminNavSection, ShellNavItem } from '@bar/ui/basis';
import type { NavItem } from '@web/registry/types';

/** true, wenn `pathname` unter `base` liegt (`/` nur exakt). */
export function pathMatches(pathname: string, base: string): boolean {
  const clean = base.length > 1 ? base.replace(/\/$/, '') : base;
  if (clean === '/') return pathname === '/';
  return pathname === clean || pathname.startsWith(`${clean}/`);
}

/**
 * Schlüssel des aktiven Eintrags: der längste passende Pfad (inkl. `activeFor`) gewinnt,
 * damit `/people/me` vor `/people` liegt. `aliases` übersetzt z. B. `/people/<eigene id>` → `/people/me`.
 */
export function activeNavKey(
  items: readonly Pick<NavItem, 'key' | 'path' | 'activeFor' | 'external'>[],
  pathname: string,
  aliases: Readonly<Record<string, string>> = {},
): string | undefined {
  let path = pathname;
  for (const [from, to] of Object.entries(aliases)) {
    if (pathMatches(path, from)) path = to + path.slice(from.length);
  }
  let best: { key: string; length: number } | undefined;
  for (const item of items) {
    if (item.external) continue;
    for (const candidate of [item.path, ...(item.activeFor ?? [])]) {
      if (pathMatches(path, candidate) && (!best || candidate.length > best.length)) {
        best = { key: item.key, length: candidate.length };
      }
    }
  }
  return best?.key;
}

/** Titel der Topbar ohne PageHeader: Label des aktiven Eintrags, sonst der Plattformname (§3.1.3). */
export function fallbackTitleFor(
  items: readonly Pick<NavItem, 'key' | 'label'>[],
  activeKey: string | undefined,
  platformName: string,
): string {
  return items.find((item) => item.key === activeKey)?.label ?? platformName;
}

export function toShellItems(
  items: readonly NavItem[],
  activeKey: string | undefined,
  badges: Readonly<Record<string, number>> = {},
): ShellNavItem[] {
  return items.map((item) => ({
    id: item.key,
    label: item.label,
    icon: item.icon,
    href: item.path,
    reload: item.external,
    active: item.key === activeKey,
    badge: badges[item.key],
    tint: item.path.startsWith('/ai') && item.external ? 'ai' : undefined,
  }));
}

export function toAdminSections(
  sections: readonly { area: string; items: readonly NavItem[] }[],
  pathname: string,
): AdminNavSection[] {
  const active = activeNavKey(
    sections.flatMap((s) => s.items),
    pathname,
  );
  return sections.map((section) => ({
    area: section.area,
    items: toShellItems(section.items, active),
  }));
}

const OTHER_PREFIXES = ['/admin', '/ai'] as const;

/**
 * Ziel eines Registry-Befehls bzw. Suchtreffers (`href` relativ zur Plattform oder absolut mit
 * Plattform-Präfix): fremde Plattform → volle Navigation, eigenes Präfix wird abgeschnitten.
 */
export function commandTarget(
  href: string,
  ownPrefix: '' | '/admin' | '/ai',
): { href: string; reload: boolean } {
  const under = (prefix: string) => href === prefix || href.startsWith(`${prefix}/`);
  if (ownPrefix && under(ownPrefix))
    return { href: href.slice(ownPrefix.length) || '/', reload: false };
  if (OTHER_PREFIXES.some((p) => p !== ownPrefix && under(p))) return { href, reload: true };
  return { href, reload: false };
}
